import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  GetOrgIntegrationSettingsPayload,
  GetOrgIntegrationSettingsResponse,
} from "../types/get-org-integration-settings.types";
import { GenericAPIResponse } from "../../services/utils";
import { LeadStage } from "./types/get-org-lead-stages.types";
import { LeadGroup } from "./types/get-org-lead-groups.types";
import { LeadStageType } from "./types/get-lead-stage.types";
import {
  CreateLeadStagePayload,
  CreateLeadStageResponse,
} from "./types/create-lead-stage.types";
import { LeadGroupType } from "./types/get-lead-groups.types";
import {
  UpdateLeadStagePayload,
  UpdateLeadStageResponse,
} from "./types/update-lead-stage.types";
import {
  ChangeLeadStagePositionPayload,
  ChangeLeadStagePositionResponse,
} from "./types/change-lead-stage-position.types";
import {
  CreateLeadGroupPayload,
  CreateLeadGroupResponse,
} from "./types/create-lead-group.types";
import {
  UpdateLeadGroupPayload,
  UpdateLeadGroupResponse,
} from "./types/update-lead-group.types";
import { CustomerFilter } from "./types/customer-search.types";
import { Category } from "./types/category.types";
import { CafFormStatus, CafFormView } from "./types/caf-form-review.types";
import { WhatsappData } from "./types/get-whatsapp-data.types";
import { Plan } from "./types/plan.types";
import { GetSubscriptionResponse } from "./types/get-subscription.types";
import {
  CreateSubscriptionPayload,
  CreateSubscriptionResponse,
} from "./types/create-subscription.types";
import { AddOrgNotePayload } from "./types/add-org-note.types";
import { CreateBankPaymentPayload } from "./types/create-bank-payment.types";
import { EndSubscriptionResponse } from "./types/end-subscription.types";
import { UserDetails } from "./types/get-user-details.types";
import {
  GetCallHistoryPayload,
  GetCallHistoryResponse,
} from "./types/get-call-history.types";
import { convertToSearchParams } from "../../utility/convert-to-search-params";
import { GetTrialCallHistoryPayload } from "./types/get-trial-call-history.types";
import { getTrialHistoryApiFilter } from "../../views/superfone/call-history/store/action";
import { UpdateOrgPayload } from "./types/update-org.types";
import { CreateOrgPayload } from "./types/create-org.types";
import {
  PresignURLPayload,
  PresignURLResponse,
  GetPublicURLPayload,
  GetPublicURLResponse,
} from "./types/presign-url.types";
import {
  GetDialerCdrListPayload,
  GetDialerCdrListResponse,
} from "./types/get-dialer-cdr-list.types";
import {
  OnboardingWebhookRequestPayload,
  OnboardingWebhookRequestResponse,
} from "./types/onboarding-webhook.types";
import {
  GetAIAgentKnowledgeBasePayload,
  GetAIAgentKnowledgeBaseResponse,
  UpdateAIAgentKnowledgeBasePayload,
  UpdateAIAgentKnowledgeBaseResponse,
} from "./types/ai-agent-knowledge-base.types";
import {
  KBAssistantType,
  KBChangelogItem,
  KBFileStatusResponse,
  KBFileStatusRow,
  KBFilesResponse,
  KBSendMessageResponse,
  KBSessionResponse,
  KBUploadUrlResponse,
  PublishKBResponse,
  ReviewKBPayload,
  ReviewKBResponse,
  UploadKBFilePayload,
  UploadKBFileResponse,
} from "./types/kb-trainer.types";
import {
  CreateIntegrationPayload,
  CreateIntegrationResponse,
} from "./types/create-integration.types";
import {
  OrgLabel,
  CreateOrgLabelPayload,
  UpdateOrgLabelPayload,
} from "./types/org-label.types";

export interface OrgMessagingNumber {
  voip_number_id: number;
  voip_number: string;
  provider: string;
  country_code: string | null;
  number_status: string;
  // null when the number was never enabled for messaging (no config row).
  messaging: {
    status: string;
    compliance_status: "pending" | "registered" | "exempt" | "failed" | null;
    capabilities: { sms?: boolean; mms?: boolean; rcs?: boolean } | null;
    is_isolated: boolean | null;
    provider_config_ref: string | null;
  } | null;
}

export interface OrgMessagingNumbersResponse {
  org_id: number;
  numbers: OrgMessagingNumber[];
}

/**
 * GET /files answers with either a bare array or `{ files: [...] }` depending on the
 * code path — normalise so callers only ever see `{ files }`.
 */
const normalizeKBFiles = (data: any): KBFilesResponse => {
  if (Array.isArray(data)) return { files: data };
  if (Array.isArray(data?.files)) return { files: data.files };
  return { files: [] };
};

export const superfoneApi = createApi({
  reducerPath: "superfoneApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api",
    credentials: "include",
  }),
  endpoints: (builder) => ({
    getStaticAIVoices: builder.query<any, number>({
      query: (orgId) => ({
        url: `/admin/orangutan/static-ai-voices?org_id=${orgId}`,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getOrgIntegrationSettings: builder.query<
      GetOrgIntegrationSettingsResponse,
      GetOrgIntegrationSettingsPayload
    >({
      query: (payload) => {
        const { orgId, page, page_size } = payload;
        return {
          url: `/admin/integration/setting/org/${orgId}?page=${
            page || 1
          }&page_size=${page_size || 10}`,
        };
      },
      transformResponse: (
        response: GenericAPIResponse<GetOrgIntegrationSettingsResponse>,
      ) => {
        return response.data;
      },
    }),
    createIntegration: builder.mutation<
      CreateIntegrationResponse,
      CreateIntegrationPayload
    >({
      query: (body) => ({
        url: `/admin/integration`,
        method: "post",
        body,
      }),
      transformResponse: (
        response: GenericAPIResponse<CreateIntegrationResponse>,
      ) => response.data,
    }),
    getLeadStageTypes: builder.query<LeadStageType[], void>({
      query: () => ({
        url: `/lead-stage/types`,
      }),
      transformResponse: (response: GenericAPIResponse<LeadStageType[]>) =>
        response.data,
    }),
    getLeadGroupTypes: builder.query<LeadGroupType[], void>({
      query: () => ({
        url: `/lead-group/types`,
      }),
      transformResponse: (response: GenericAPIResponse<LeadGroupType[]>) =>
        response.data,
    }),
    createLeadStage: builder.mutation<
      CreateLeadStageResponse,
      CreateLeadStagePayload
    >({
      query: (body) => ({
        url: `/admin/lead-stage`,
        method: "post",
        body,
      }),
      transformResponse: (
        response: GenericAPIResponse<CreateLeadStageResponse>,
      ) => response.data,
    }),
    updateLeadStage: builder.mutation<
      UpdateLeadStageResponse,
      UpdateLeadStagePayload
    >({
      query: (payload) => {
        const { id, ...body } = payload;
        return {
          url: `/admin/lead-stage/${id}`,
          method: "put",
          body,
        };
      },
      transformResponse: (
        response: GenericAPIResponse<UpdateLeadStageResponse>,
      ) => response.data,
    }),
    changeLeadStagePosition: builder.mutation<
      ChangeLeadStagePositionResponse,
      ChangeLeadStagePositionPayload
    >({
      query: (payload) => {
        const { id, ...body } = payload;
        return {
          url: `/admin/lead-stage/${id}/change-position`,
          method: "put",
          body,
        };
      },
      transformResponse: (
        response: GenericAPIResponse<ChangeLeadStagePositionResponse>,
      ) => response.data,
    }),
    createLeadGroup: builder.mutation<
      CreateLeadGroupResponse,
      CreateLeadGroupPayload
    >({
      query: (body) => {
        return {
          url: `/admin/lead-group`,
          method: "post",
          body,
        };
      },
      transformResponse: (
        response: GenericAPIResponse<CreateLeadGroupResponse>,
      ) => response.data,
    }),
    updateLeadGroup: builder.mutation<
      UpdateLeadGroupResponse,
      UpdateLeadGroupPayload
    >({
      query: (payload) => {
        const { id, ...body } = payload;
        return {
          url: `/admin/lead-group/${id}`,
          method: "put",
          body,
        };
      },
      transformResponse: (
        response: GenericAPIResponse<UpdateLeadGroupResponse>,
      ) => response.data,
    }),
    getOrgLeadStages: builder.query<LeadStage[], number>({
      query: (orgId) => ({
        url: `/admin/lead-stage/org/${orgId}`,
      }),
      transformResponse: (response: GenericAPIResponse<LeadStage[]>) => {
        return response.data;
      },
    }),
    getOrgLeadGroups: builder.query<LeadGroup[], number>({
      query: (orgId) => ({
        url: `/admin/lead-group/org/${orgId}`,
      }),
      transformResponse: (response: GenericAPIResponse<LeadGroup[]>) => {
        return response.data;
      },
    }),
    getOrgLabels: builder.query<OrgLabel[], number>({
      query: (orgId) => ({
        url: `/admin/label/org/${orgId}`,
      }),
      transformResponse: (response: GenericAPIResponse<OrgLabel[]>) => {
        return response.data;
      },
    }),
    createOrgLabel: builder.mutation<OrgLabel, CreateOrgLabelPayload>({
      query: (body) => ({
        url: `/admin/label`,
        method: "post",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<OrgLabel>) =>
        response.data,
    }),
    updateOrgLabel: builder.mutation<OrgLabel, UpdateOrgLabelPayload>({
      query: ({ id, ...body }) => ({
        url: `/admin/label/${id}`,
        method: "put",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<OrgLabel>) =>
        response.data,
    }),
    deleteOrgLabel: builder.mutation<OrgLabel, number>({
      query: (id) => ({
        url: `/admin/label/${id}`,
        method: "delete",
      }),
      transformResponse: (response: GenericAPIResponse<OrgLabel>) =>
        response.data,
    }),
    deleteLeadStage: builder.mutation<any, number>({
      query: (id) => {
        return {
          url: `/admin/lead-stage/${id}`,
          method: "delete",
        };
      },
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    deleteLeadGroup: builder.mutation<any, number>({
      query: (id) => {
        return {
          url: `/admin/lead-group/${id}`,
          method: "delete",
        };
      },
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getCustomerFilters: builder.query<CustomerFilter[], void>({
      query: () => {
        return {
          url: "/customer/filters",
        };
      },
      transformResponse: (response: GenericAPIResponse<CustomerFilter[]>) =>
        response.data,
    }),
    getCategories: builder.query<Category[], void>({
      query: () => ({ url: "/categories" }),
      transformResponse: (response: GenericAPIResponse<Category[]>) =>
        response.data,
    }),
    getWhatsappData: builder.query<WhatsappData, number>({
      query: (orgId) => ({
        url: `/whatsapp/heltar/${orgId}`,
      }),
      transformResponse: (response: GenericAPIResponse<WhatsappData>) =>
        response.data,
    }),
    getAllPlans: builder.query<Plan[], void>({
      query: () => ({
        url: "subscription/plans/all",
      }),
      transformResponse: (response: GenericAPIResponse<Plan[]>) =>
        response.data,
    }),
    getSubscriptionById: builder.query<GetSubscriptionResponse, number>({
      query: (subscriptionId: number) => ({
        url: `subscription/${subscriptionId}`,
      }),
      transformResponse: (
        response: GenericAPIResponse<GetSubscriptionResponse>,
      ) => response.data,
    }),
    endSubscription: builder.mutation<EndSubscriptionResponse, number>({
      query: (subscriptionId) => ({
        url: `/subscription/end/${subscriptionId}`,
        method: "post",
      }),
      transformResponse: (
        response: GenericAPIResponse<EndSubscriptionResponse>,
      ) => response.data,
    }),
    createSubscription: builder.mutation<
      CreateSubscriptionResponse,
      CreateSubscriptionPayload
    >({
      query: (body) => ({
        url: "/subscription",
        method: "post",
        body,
      }),
      transformResponse: (
        response: GenericAPIResponse<CreateSubscriptionResponse>,
      ) => response.data,
    }),
    addOrgNote: builder.mutation<any, AddOrgNotePayload>({
      query: (payload) => {
        const note = `Action: ${payload.action_name}, comment: ${payload.comment}`;
        return {
          url: `/org/notes`,
          method: "post",
          body: {
            org_id: payload.org_id,
            note,
          },
        };
      },
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    createBankPayment: builder.mutation<any, CreateBankPaymentPayload>({
      query: (body) => ({
        url: "/payments/bank-payment",
        method: "post",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getUserDetails: builder.query<UserDetails, number>({
      query: (userId) => ({
        url: `/users/${userId}`,
      }),
      transformResponse: (response: GenericAPIResponse<UserDetails>) =>
        response.data,
    }),
    deletePayment: builder.mutation<any, number>({
      query: (paymentId) => ({
        url: `/orders/payments/${paymentId}`,
        method: "delete",
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    refundBankPayment: builder.mutation<
      any,
      { payment_id: any; org_id: number }
    >({
      query: (body) => ({
        url: `/payments/bank-payment`,
        method: "delete",
        body: body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getCallHistory: builder.query<
      GetCallHistoryResponse,
      GetCallHistoryPayload
    >({
      query: (payload) => {
        const { org_id, ...rest } = payload;
        const query = convertToSearchParams(rest);
        return {
          url: `/call-history/org/${org_id}${query}`,
        };
      },
      transformResponse: (
        response: GenericAPIResponse<GetCallHistoryResponse>,
      ) => response.data,
    }),
    getTrialCallHistory: builder.query<
      GetCallHistoryResponse,
      GetTrialCallHistoryPayload
    >({
      query: (payload) => {
        const {
          durationRange,
          page_size,
          text,
          status,
          call_type,
          include_soft_deleted,
        } = payload;
        const apiFilter = getTrialHistoryApiFilter(durationRange);

        const query = convertToSearchParams({
          page_size,
          text,
          status,
          call_type,
          include_soft_deleted,
          ...apiFilter,
        });
        return {
          url: `/call-history/trial${query}`,
        };
      },
      transformResponse: (
        response: GenericAPIResponse<GetCallHistoryResponse>,
      ) => response.data,
    }),
    createOrg: builder.mutation<any, CreateOrgPayload>({
      query: (body) => {
        return { url: `/admin/org`, method: "post", body };
      },
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    updateOrg: builder.mutation<any, UpdateOrgPayload>({
      query: (payload) => {
        const { org_id, ...body } = payload;
        return { url: `/org/${org_id}`, method: "put", body };
      },
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    startCRMTrial: builder.mutation<
      any,
      {
        org_id: number;
        start_date: string;
        valid_till: string;
      }
    >({
      query: (body) => ({
        url: `admin/org/start-crm-trial`,
        method: "post",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    fupToggle: builder.mutation<
      any,
      { org_id: number; is_fup_kyc_required: boolean }
    >({
      query: (payload) => {
        const { org_id, ...body } = payload;
        return {
          url: `/org/kyc/${org_id}/fup/toggle`,
          method: "post",
          body,
        };
      },
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    // Lists an org's VOIP numbers + their messaging/compliance config so CX can
    // mark a number SMS-exempt (compliance_status=exempt). Super-admin only.
    getOrgMessagingNumbers: builder.query<OrgMessagingNumbersResponse, number>({
      query: (org_id) => ({
        url: `/v1/messaging/admin/orgs/${org_id}/numbers`,
        method: "get",
      }),
      transformResponse: (
        response: GenericAPIResponse<OrgMessagingNumbersResponse>,
      ) => response.data,
    }),
    // Flip a number's messaging compliance_status. CX uses status='exempt' to
    // let it send SMS without 10DLC registration; US numbers need allowUsExempt
    // (verified toll-free) or the API rejects with 409.
    setNumberSmsCompliance: builder.mutation<
      any,
      {
        voip_number_id: number;
        status: "exempt" | "registered" | "pending" | "failed";
        allowUsExempt?: boolean;
        metadata?: Record<string, unknown>;
      }
    >({
      query: ({ voip_number_id, ...body }) => ({
        url: `/v1/messaging/admin/numbers/${voip_number_id}/compliance`,
        method: "post",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    updateFupKYC: builder.mutation<
      any,
      { org_id: number; fup_status?: string; pofup?: string }
    >({
      query: (body) => ({
        url: `/org/kyc/modify`,
        method: "put",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    presignURL: builder.mutation<PresignURLResponse, PresignURLPayload>({
      query: (payload) => ({
        url: `/s3/presignURL?file_name=${payload.file_name}&file_type=${
          payload.file_type
        }${payload?.public ? `&public=true` : ""}`,
        method: "get",
      }),
      transformResponse: (response: GenericAPIResponse<PresignURLResponse>) =>
        response.data,
    }),
    copyFUPKYC: builder.mutation<
      any,
      {
        source_org_id: number;
        destination_org_id: number;
        comment: string;
      }
    >({
      query: (body) => ({
        url: `/admin/org/kyc/copy/fup`,
        body,
        method: "post",
      }),
      transformResponse: (response: any) => response.data,
    }),
    getDigioRequestsForFUP: builder.query<any, number>({
      query: (org_id) => ({
        url: `digio/org/${org_id}/fup`,
      }),
      transformResponse: (response: any) => response.data,
    }),
    createDigioFUPRequest: builder.mutation<
      any,
      {
        org_id: number;
        email: string;
      }
    >({
      query: (body) => ({
        url: `/admin/org/kyc/digio/fup`,
        method: "post",
        body,
      }),
      transformResponse: (response: any) => response.data,
    }),
    // Initiates a Digio CAF (Customer Application Form) e-sign request. Digio
    // emails the sign link to the given address itself, so no token comes back.
    // Requires both user KYC and business KYC to already be approved.
    createDigioCAFRequest: builder.mutation<
      any,
      {
        org_id: number;
        email: string;
      }
    >({
      query: (body) => ({
        url: `/admin/org/kyc/digio/caf`,
        method: "post",
        body,
      }),
      transformResponse: (response: any) => response.data,
    }),
    // v2 of the above, through the CAF confirmation flow: prints the details the owner confirmed and
    // is refused while an owner's edit is waiting on (or was rejected by) the BV team. Does not
    // require the owner to have confirmed — CX can still send one for an org that never did.
    createDigioCAFRequestV2: builder.mutation<
      any,
      {
        org_id: number;
        email: string;
      }
    >({
      query: (body) => ({
        url: `/admin/org/kyc/v2/digio/caf`,
        method: "post",
        body,
      }),
      transformResponse: (response: any) => response.data,
    }),
    // CAF status + freshly re-signed S3 URLs. 404s when the org has no KYC row.
    // Also carries the confirmation state, since a CAF that has not been generated is usually
    // waiting on the subscriber or on this team rather than on Digio.
    getCAFDocuments: builder.query<
      {
        org_id: number;
        caf_status: string | null;
        caf_signed_url: string | null;
        caf_final_url: string | null;
        caf_form_status: CafFormStatus | null;
        caf_form_pending_changes: number;
      },
      number
    >({
      query: (org_id) => ({
        url: `/admin/org/kyc/digio/caf/documents/${org_id}`,
        method: "get",
      }),
      transformResponse: (response: any) => response.data,
    }),
    // What the subscriber was shown on the CAF confirmation screen, what they changed, and
    // where the submission stands. 400s until user KYC and business KYC are approved, since
    // every value on the form is derived from them.
    getCafFormReview: builder.query<CafFormView, number>({
      query: (org_id) => ({
        url: `/admin/org/${org_id}/kyc/caf/form`,
        method: "get",
      }),
      transformResponse: (response: any) => response.data,
    }),
    // The verdict on a pending set of CAF edits, field by field. All approved also generates the
    // CAF with the corrected details, so `generation_error` may come back set on an otherwise
    // successful approval; any rejected sends it back to the customer and generates nothing.
    reviewCafForm: builder.mutation<
      { status: CafFormStatus; caf_request?: any; generation_error?: string },
      {
        org_id: number;
        /** One per change awaiting review; a rejection needs its own reason. */
        decisions?: { field: string; action: "APPROVED" | "REJECTED"; rejection_reason?: string }[];
        /** The whole set at once, instead of `decisions`. */
        action?: "APPROVED" | "REJECTED";
        rejection_reason?: string;
        email?: string;
        /** Approving only: CX's corrections to the customer's submitted values. */
        values?: Record<string, string>;
      }
    >({
      query: ({ org_id, ...body }) => ({
        url: `/admin/org/${org_id}/kyc/caf/form/review`,
        method: "post",
        body,
      }),
      transformResponse: (response: any) => response.data,
    }),
    // CX corrects the CAF details directly (editable fields only). Saved as approved — no review —
    // and any unsigned CAF built from the old details is expired. Refused while the customer's own
    // edits await review. Returns the updated form.
    saveCafFormByAdmin: builder.mutation<CafFormView, { org_id: number; values: Record<string, string> }>({
      query: ({ org_id, ...body }) => ({
        url: `/admin/org/${org_id}/kyc/caf/form`,
        method: "put",
        body,
      }),
      transformResponse: (response: any) => response.data,
    }),
    downloadFUPDocument: builder.mutation<
      any,
      {
        document_id: string;
      }
    >({
      query: (body) => ({
        url: `/admin/org/kyc/digio/fup/download`,
        method: "post",
        body,
      }),
      transformResponse: (response: any) => response.data,
    }),
    getDialerCdrList: builder.query<
      GetDialerCdrListResponse,
      GetDialerCdrListPayload
    >({
      query: (payload) => {
        return {
          url: `/admin/dialer/cdr/list`,
          method: "post",
          body: payload,
        };
      },
      transformResponse: (
        response: GenericAPIResponse<GetDialerCdrListResponse>,
      ) => response.data,
    }),
    getPublicURL: builder.query<GetPublicURLResponse, GetPublicURLPayload>({
      query: (payload) => ({
        url: `/s3/getObject?file_name=${payload.filename}${
          payload.no_redirect ? `&no_redirect=${payload.no_redirect}` : ""
        }`,
      }),
      transformResponse: (response: GenericAPIResponse<GetPublicURLResponse>) =>
        response.data,
    }),
    unlinkSimPhone: builder.mutation<any, { id: number }>({
      query: (body) => ({
        url: `admin/user-sim-phone-connection/${body.id}/unlink`,
        method: "post",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    removeForwarding: builder.mutation<any, { id: number }>({
      query: (body) => ({
        url: `admin/send-pn`,
        method: "post",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    listMandates: builder.mutation<any, { org_id: number }>({
      query: (body) => ({
        url: `admin/mandate/search`,
        method: "post",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    cancelMandate: builder.mutation<
      any,
      { org_id: number; mandate_id: number }
    >({
      query: (body) => ({
        url: `admin/mandate/${body.org_id}/${body.mandate_id}/cancel`,
        method: "post",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    createMandate: builder.mutation<any, { org_id: number }>({
      query: (body) => ({
        url: `admin/mandate/create-registration-link`,
        method: "post",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    mandateById: builder.query<any, { mandate_id: number }>({
      query: (body) => ({
        url: `admin/mandate/${body.mandate_id}`,
        method: "get",
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    onBoardingWebHook: builder.mutation<any, OnboardingWebhookRequestPayload>({
      query: (body) => ({
        url: "/webhook/01bdbc69-1b37-4188-a796-39582a7bb8de/onboard",
        method: "post",
        body,
      }),
      transformResponse: (
        response: GenericAPIResponse<OnboardingWebhookRequestResponse>,
      ) => response.data,
    }),
    getGMBLocationsByOrg: builder.query<any, { orgId: string; userId: string }>(
      {
        query: ({ orgId, userId }) => ({
          url: `/gmb-locations/admin/org?org_id=${orgId}&user_id=${userId}`,
          method: "get",
        }),
        transformResponse: (response: GenericAPIResponse<any>) => response.data,
      },
    ),
    unlinkGmbAccount: builder.mutation<any, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/gmb-locations/admin/delete-gmb-data?org_id=${orgId}`,
        method: "delete",
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    syncGMBAccount: builder.mutation<any, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `gmb-locations/admin/sync?org_id=${orgId}`,
        method: "post",
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getAIAgentKnowledgeBase: builder.query<
      GetAIAgentKnowledgeBaseResponse,
      GetAIAgentKnowledgeBasePayload
    >({
      query: (payload) => {
        const { org_id, trunk_number } = payload;
        const url = trunk_number
          ? `/settings/admin/ai-agent-knowledge-base/${org_id}?trunk_number=${trunk_number}`
          : `/settings/admin/ai-agent-knowledge-base/${org_id}`;
        return { url };
      },
      transformResponse: (
        response: GenericAPIResponse<GetAIAgentKnowledgeBaseResponse>,
      ) => response.data,
    }),
    updateAIAgentKnowledgeBase: builder.mutation<
      UpdateAIAgentKnowledgeBaseResponse,
      UpdateAIAgentKnowledgeBasePayload
    >({
      query: (payload) => {
        const { org_id, trunk_number, ...body } = payload;
        const url = trunk_number
          ? `/settings/admin/ai-agent-knowledge-base/${org_id}?trunk_number=${trunk_number}`
          : `/settings/admin/ai-agent-knowledge-base/${org_id}`;
        return {
          url,
          method: "put",
          body,
        };
      },
      transformResponse: (
        response: GenericAPIResponse<UpdateAIAgentKnowledgeBaseResponse>,
      ) => response.data,
    }),
    // ========================================================================
    // KB trainer (admin) — /kb-trainer/admin/:org_id/...
    // ------------------------------------------------------------------------
    // Mirrors the customer endpoints llama calls, scoped to a target org by path
    // param. Powers the document/website upload → review → conflict flow in AI
    // Receptionist settings. Every call takes orgId; there is no session org here.
    // ========================================================================
    // `assistantType` picks which agent is being trained. The backend keeps the two
    // knowledge bases fully isolated, so this also decides which KB a publish writes to.
    // Omitted → receptionist, matching the backend default.
    createAdminKBSession: builder.mutation<
      KBSessionResponse,
      { orgId: number; assistantType?: KBAssistantType }
    >({
      query: ({ orgId, assistantType }) => ({
        url: `/kb-trainer/admin/${orgId}/sessions`,
        method: "post",
        body: assistantType ? { assistant_type: assistantType } : {},
      }),
      transformResponse: (response: GenericAPIResponse<KBSessionResponse>) =>
        response.data,
    }),
    getAdminKBUploadUrl: builder.query<
      KBUploadUrlResponse,
      { orgId: number; sessionId: number; filename: string; mime_type: string }
    >({
      query: ({ orgId, sessionId, filename, mime_type }) => ({
        url: `/kb-trainer/admin/${orgId}/sessions/${sessionId}/upload-url?filename=${encodeURIComponent(
          filename,
        )}&mime_type=${encodeURIComponent(mime_type)}`,
      }),
      transformResponse: (response: GenericAPIResponse<KBUploadUrlResponse>) =>
        response.data,
    }),
    // Returns 202 in ms — extraction is only enqueued. Poll getAdminKBFileStatus
    // with the returned message_id until it settles.
    uploadAdminKBFile: builder.mutation<
      UploadKBFileResponse,
      { orgId: number; sessionId: number; payload: UploadKBFilePayload }
    >({
      query: ({ orgId, sessionId, payload }) => ({
        url: `/kb-trainer/admin/${orgId}/sessions/${sessionId}/files`,
        method: "post",
        body: payload,
      }),
      transformResponse: (
        response: GenericAPIResponse<{
          message_id: number;
          source: string;
          extraction_status: string;
          processing: boolean;
        }>,
      ): UploadKBFileResponse => {
        const { message_id, source, extraction_status, processing } =
          response.data;
        return {
          message_id,
          source,
          extraction_status,
          isProcessing: processing,
        };
      },
    }),
    // Website links skip S3 entirely — the backend scrapes the URL from `content`.
    // Unlike POST /files this is synchronous: the scrape, two summarisation LLM calls and
    // the suggestion pass all run inside the request, so it can sit open for the better
    // part of a minute. Callers must budget for that rather than treating it as a 202.
    sendAdminKBMessage: builder.mutation<
      KBSendMessageResponse,
      {
        orgId: number;
        sessionId: number;
        payload: { source?: "upload"; content?: string | null };
      }
    >({
      query: ({ orgId, sessionId, payload }) => ({
        url: `/kb-trainer/admin/${orgId}/sessions/${sessionId}/messages`,
        method: "post",
        body: payload,
      }),
      transformResponse: (
        response: GenericAPIResponse<KBSendMessageResponse>,
      ) => response.data,
    }),
    getAdminKBFiles: builder.query<
      KBFilesResponse,
      { orgId: number; sessionId: number }
    >({
      query: ({ orgId, sessionId }) => ({
        url: `/kb-trainer/admin/${orgId}/sessions/${sessionId}/files`,
      }),
      transformResponse: (response: GenericAPIResponse<any>) =>
        normalizeKBFiles(response.data),
    }),
    // Single-file poll target — the same /files endpoint under `view=status`, which drops
    // the signed preview URL and the audio transcript. Both are pure waste at a 2s poll
    // interval, and the client already has them from its initial listFiles call.
    getAdminKBFileStatus: builder.query<
      KBFileStatusResponse,
      { orgId: number; sessionId: number; messageId: number }
    >({
      query: ({ orgId, sessionId, messageId }) => ({
        url: `/kb-trainer/admin/${orgId}/sessions/${sessionId}/files?view=status&message_id=${messageId}`,
      }),
      transformResponse: (
        response: GenericAPIResponse<any>,
      ): KBFileStatusResponse => {
        const { files } = normalizeKBFiles(response.data);
        return {
          files: files as unknown as KBFileStatusRow[],
          all_settled: response.data?.all_settled ?? true,
        };
      },
    }),
    deleteAdminKBFile: builder.mutation<
      unknown,
      { orgId: number; sessionId: number; messageId: number }
    >({
      query: ({ orgId, sessionId, messageId }) => ({
        url: `/kb-trainer/admin/${orgId}/sessions/${sessionId}/files/${messageId}`,
        method: "delete",
      }),
    }),
    // Cursor-paginated at 50 a page. Reading only the first page silently caps review
    // at 50 items, so publish then fails with "Resolve N pending items" and no amount of
    // clicking Save clears it — the unreviewed remainder was never on screen. One website
    // crawl can easily produce 100+ suggestions, so this must follow the cursor.
    getAdminKBChangelog: builder.query<
      KBChangelogItem[],
      { orgId: number; sessionId: number; status?: string }
    >({
      queryFn: async (
        { orgId, sessionId, status },
        _api,
        _opts,
        fetchWithBQ,
      ) => {
        // The API has shipped four wrappers for this list over time; accept all.
        const rowsOf = (data: any): KBChangelogItem[] => {
          if (Array.isArray(data)) return data;
          for (const key of ["entries", "changelog", "changes"]) {
            if (Array.isArray(data?.[key])) return data[key];
          }
          return [];
        };

        const all: KBChangelogItem[] = [];
        let cursor: string | undefined;
        // Bounded so a server that always reports has_more can't spin forever.
        for (let page = 0; page < 40; page++) {
          const qs = new URLSearchParams({ limit: "100" });
          if (status) qs.set("status", status);
          if (cursor) qs.set("cursor", cursor);

          const res = await fetchWithBQ(
            `/kb-trainer/admin/${orgId}/sessions/${sessionId}/changelog?${qs}`,
          );
          if (res.error) return { error: res.error };

          const data = (res.data as GenericAPIResponse<any>)?.data;
          all.push(...rowsOf(data));

          if (!data?.has_more || !data?.next_cursor) break;
          cursor = data.next_cursor;
        }

        return { data: all };
      },
    }),
    reviewAdminKBChanges: builder.mutation<
      ReviewKBResponse,
      { orgId: number; sessionId: number; payload: ReviewKBPayload }
    >({
      query: ({ orgId, sessionId, payload }) => ({
        url: `/kb-trainer/admin/${orgId}/sessions/${sessionId}/review`,
        method: "post",
        body: payload,
      }),
      transformResponse: (response: GenericAPIResponse<ReviewKBResponse>) =>
        response.data,
    }),
    publishAdminKBSession: builder.mutation<
      PublishKBResponse,
      { orgId: number; sessionId: number; payload?: { force?: boolean } }
    >({
      query: ({ orgId, sessionId, payload }) => ({
        url: `/kb-trainer/admin/${orgId}/sessions/${sessionId}/publish`,
        method: "post",
        body: payload || {},
      }),
      transformResponse: (response: GenericAPIResponse<PublishKBResponse>) =>
        response.data,
    }),
    getAiSaleAgentKnowledgeBase: builder.query<any, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/settings/admin/ai-sale-agent-knowledge-base/${orgId}`,
        method: "get",
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    updateAiSaleAgentKnowledgeBase: builder.mutation<
      any,
      { orgId: string; body: any }
    >({
      query: ({ orgId, body }) => ({
        url: `/settings/admin/ai-sale-agent-knowledge-base/${orgId}`,
        method: "put",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getAiPersonalAgentKnowledgeBase: builder.query<any, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/settings/admin/ai-personal-agent-knowledge-base/${orgId}`,
        method: "get",
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    updateAiPersonalAgentKnowledgeBase: builder.mutation<
      any,
      { orgId: string; body: any }
    >({
      query: ({ orgId, body }) => ({
        url: `/settings/admin/ai-personal-agent-knowledge-base/${orgId}`,
        method: "put",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    syncAiPersonalAgentJobsWithTemplate: builder.mutation<any, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/settings/admin/ai-personal-agent-knowledge-base/${orgId}/sync-template`,
        method: "post",
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    updateAiPersonalAgentJobHints: builder.mutation<
      any,
      { orgId: string; jobId: string; body: any }
    >({
      query: ({ orgId, jobId, body }) => ({
        url: `/settings/admin/ai-personal-agent-knowledge-base/${orgId}/jobs/${jobId}/hints`,
        method: "put",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getOrgUsers: builder.query<any, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/users/org/${orgId}`,
        method: "get",
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    updateUsers: builder.mutation<
      any,
      { id: number; body: Record<string, any> }
    >({
      query: ({ id, body }) => ({
        url: `/users/${id}`,
        method: "put",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getCustomerCustomFieldLabels: builder.query<any, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/admin/customer-custom-field-label/${orgId}`,
        method: "get",
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getWalletBalanceData: builder.query<any, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/admin/wallet/${orgId}/balance`,
        method: "get",
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    createGlobalWalletTopupOrder: builder.mutation<
      any,
      { org_id: number; amount: number; payment_id?: string; gstin?: string }
    >({
      query: ({ org_id, amount, payment_id, gstin }) => {
        return {
          url: `/admin/wallet/${org_id}/manually-add-credit`,
          method: "post",
          body: {
            amount,
            ...(payment_id ? { payment_id } : {}),
            ...(gstin ? { gstin } : {}),
          },
        };
      },
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getWalletTransactions: builder.mutation<
      any,
      {
        orgId: string;
        from_date: string;
        to_date: string;
        limit: number;
        cursor: number;
        triggered_by_user_ids: number[];
        bucket_type?: string;
        service_type?: string;
      }
    >({
      query: ({
        orgId,
        from_date,
        to_date,
        limit,
        cursor,
        triggered_by_user_ids,
        bucket_type,
        service_type,
      }) => ({
        url: `/admin/wallet/${orgId}/transactions`,
        method: "post",
        body: {
          limit,
          cursor,
          from_date,
          to_date,
          triggered_by_user_ids,
          bucket_type,
          service_type,
        },
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    adminAdjustWalletBalance: builder.mutation<
      any,
      {
        org_id: number;
        transaction_type: string;
        bucket_type: string;
        balance_type: string;
        amount: number;
        replenishment_mode?: string;
        service_type?: string;
        description: string;
      }
    >({
      query: ({ org_id, ...body }) => ({
        url: `/admin/wallet/${org_id}/adjust-balance`,
        method: "post",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getSetupChargesPaidList: builder.query<any, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/subscription/${orgId}/setup-fees-paid`,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    paySetupFees: builder.mutation<
      any,
      {
        org_id: number;
        addons: number[];
        payment_id: string;
        gstin?: string;
      }
    >({
      query: (payload) => ({
        url: `/subscription/${payload.org_id}/create-setup-fees`,
        method: "post",
        body: payload,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getPreviewAddonCredits: builder.mutation<any, { addons: number[] }>({
      query: ({ addons }) => ({
        url: `/admin/orders/v2/preview/addon`,
        method: "post",
        body: { addons: addons },
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getAICallPackages: builder.query<any, { orgId: string | number }>({
      query: ({ orgId }) => ({
        url: `/admin/orders/v1/ai-call-packages/${orgId}`,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    createAICallPackageOrder: builder.mutation<
      any,
      {
        amount_in_paise: number;
        bonus_amount_in_paise?: number;
        org_id?: number;
        payment_id?: string | null;
        gstin?: string;
        payment_not_collected?: boolean;
      }
    >({
      query: (payload) => ({
        url: `/admin/orders/v1/ai-call-packages`,
        method: "post",
        body: payload,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getVoiceApiWalletPackages: builder.query<any, { orgId: string | number }>({
      query: ({ orgId }) => ({
        url: `/admin/orders/v1/voice-api-wallet-packages/${orgId}`,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    createVoiceApiWalletPackageOrder: builder.mutation<
      any,
      {
        amount_in_paise: number;
        bonus_amount_in_paise?: number;
        org_id?: number;
        payment_id?: string | null;
        gstin?: string;
        payment_not_collected?: boolean;
      }
    >({
      query: (payload) => ({
        url: `/admin/orders/v1/voice-api-wallet-packages`,
        method: "post",
        body: payload,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getAIAgentSetting: builder.query<any, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/dragonfly/whatsapp/waba-ai-agent/settings?org_id=${orgId}`,
        method: "get",
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    syncInvoice: builder.mutation<
      {
        payment_id: number;
        synced: boolean;
        invoice: string | null;
        refrens_invoice_id: string | null;
        refrens_invoice_url: string | null;
      },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/payments/sync-invoice/${id}`,
        method: "post",
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    toggleSvoipFeature: builder.mutation<
      any,
      { org_id: number; status: "active" | "inactive" }
    >({
      query: (body) => ({
        url: `/subscription/svoip-features/toggle`,
        method: "post",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    toggleMcpConnector: builder.mutation<
      any,
      { org_id: number; status: "active" | "inactive" }
    >({
      query: (body) => ({
        url: `/subscription/mcp-connector/toggle`,
        method: "post",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    toggleAiPersonalAgent: builder.mutation<
      any,
      { org_id: number; status: "active" | "inactive" }
    >({
      query: (body) => ({
        url: `/subscription/ai-personal-agent/toggle`,
        method: "post",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    getCrmSettingByOrg: builder.query<any, number>({
      query: (orgId) => ({
        url: `/admin/crm-setting/org/${orgId}`,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    updateCrmSetting: builder.mutation<
      any,
      { id: number; body: Record<string, any> }
    >({
      query: ({ id, body }) => ({
        url: `/admin/crm-setting/${id}`,
        method: "put",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    createV3Order: builder.mutation<
      { order_id: number; summary: { total_amount: number; currency: string } },
      {
        org_id: number;
        intent: string;
        plan_id?: number;
        variant_id?: number;
        addons?: { id: number; quantity?: number }[];
        remove_addon_ids?: number[];
        gstin?: string;
        discount?: { amount: number };
        payment_id?: string;
        expected_total?: number;
      }
    >({
      query: (body) => ({
        url: `/orders/v3/admin/checkout-and-fulfil`,
        method: "post",
        body,
      }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    adminV3Quote: builder.mutation<
      {
        currency: string;
        subscription_id?: number | null;
        line_items: {
          kind: string;
          description: string;
          name?: string;
          quantity: number;
          gross_amount: number;
          gst_rate: number;
          gst_amount: number;
          amount: number;
          action?: string;
          window?: { start: string; end: string; charged_days: number };
        }[];
        wallet_credits?: {
          kind: string;
          name: string;
          bucket_type: string;
          amount: number;
        }[];
        summary: {
          gross_amount: number;
          gst_amount: number;
          discount_amount: number;
          credit_note_amount: number;
          setup_fee_total: number;
          wallet_applied: number;
          total_amount: number;
          currency: string;
        };
      },
      {
        org_id: number;
        intent: string;
        plan_id?: number;
        variant_id?: number;
        addons?: { id: number; quantity?: number }[];
        remove_addon_ids?: number[];
        gstin?: string;
        discount?: { amount: number };
      }
    >({
      query: (body) => ({ url: `/orders/v3/admin/quote`, method: "post", body }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
    adminV3CheckoutAndFulfil: builder.mutation<
      { order_id: number; summary: { total_amount: number; currency: string } },
      {
        org_id: number;
        intent: string;
        plan_id?: number;
        variant_id?: number;
        addons?: { id: number; quantity?: number }[];
        remove_addon_ids?: number[];
        gstin?: string;
        discount?: { amount: number };
        payment_id?: string;
        expected_total?: number;
      }
    >({
      query: (body) => ({ url: `/orders/v3/admin/checkout-and-fulfil`, method: "post", body }),
      transformResponse: (response: GenericAPIResponse<any>) => response.data,
    }),
  }),
});

export const {
  useGetOrgIntegrationSettingsQuery,
  useCreateIntegrationMutation,
  useGetLeadGroupTypesQuery,
  useCreateLeadStageMutation,
  useUpdateLeadStageMutation,
  useChangeLeadStagePositionMutation,
  useCreateLeadGroupMutation,
  useUpdateLeadGroupMutation,
  useGetOrgLeadStagesQuery,
  useGetOrgLeadGroupsQuery,
  useGetOrgLabelsQuery,
  useCreateOrgLabelMutation,
  useUpdateOrgLabelMutation,
  useDeleteOrgLabelMutation,
  useLazyGetOrgLeadStagesQuery,
  useLazyGetOrgLeadGroupsQuery,
  useDeleteLeadStageMutation,
  useDeleteLeadGroupMutation,
  useGetCustomerFiltersQuery,
  useGetLeadStageTypesQuery,
  useGetCategoriesQuery,
  useGetWhatsappDataQuery,
  useGetAllPlansQuery,
  useGetSubscriptionByIdQuery,
  useEndSubscriptionMutation,
  useCreateSubscriptionMutation,
  useAddOrgNoteMutation,
  useCreateBankPaymentMutation,
  useGetUserDetailsQuery,
  useLazyGetUserDetailsQuery,
  useDeletePaymentMutation,
  useRefundBankPaymentMutation,
  useLazyGetCallHistoryQuery,
  useLazyGetTrialCallHistoryQuery,
  useUpdateOrgMutation,
  useCreateOrgMutation,
  useStartCRMTrialMutation,
  useFupToggleMutation,
  useUpdateFupKYCMutation,
  usePresignURLMutation,
  useCopyFUPKYCMutation,
  useGetDigioRequestsForFUPQuery,
  useCreateDigioFUPRequestMutation,
  useCreateDigioCAFRequestMutation,
  useCreateDigioCAFRequestV2Mutation,
  useGetCAFDocumentsQuery,
  useGetCafFormReviewQuery,
  useReviewCafFormMutation,
  useSaveCafFormByAdminMutation,
  useDownloadFUPDocumentMutation,
  useGetDialerCdrListQuery,
  useGetPublicURLQuery,
  useOnBoardingWebHookMutation,
  useUnlinkSimPhoneMutation,
  useRemoveForwardingMutation,
  useListMandatesMutation,
  useCancelMandateMutation,
  useCreateMandateMutation,
  useMandateByIdQuery,
  useGetGMBLocationsByOrgQuery,
  useUnlinkGmbAccountMutation,
  useSyncGMBAccountMutation,
  useGetAIAgentKnowledgeBaseQuery,
  useLazyGetAIAgentKnowledgeBaseQuery,
  useUpdateAIAgentKnowledgeBaseMutation,
  useGetWalletBalanceDataQuery,
  useCreateGlobalWalletTopupOrderMutation,
  useLazyGetWalletBalanceDataQuery,
  useLazyGetDialerCdrListQuery,
  useGetStaticAIVoicesQuery,
  useGetAiSaleAgentKnowledgeBaseQuery,
  useUpdateAiSaleAgentKnowledgeBaseMutation,
  useGetAiPersonalAgentKnowledgeBaseQuery,
  useUpdateAiPersonalAgentKnowledgeBaseMutation,
  useSyncAiPersonalAgentJobsWithTemplateMutation,
  useUpdateAiPersonalAgentJobHintsMutation,
  useGetCustomerCustomFieldLabelsQuery,
  useGetOrgUsersQuery,
  useUpdateUsersMutation,
  useGetWalletTransactionsMutation,
  useGetSetupChargesPaidListQuery,
  usePaySetupFeesMutation,
  useGetPreviewAddonCreditsMutation,
  useGetAICallPackagesQuery,
  useCreateAICallPackageOrderMutation,
  useGetVoiceApiWalletPackagesQuery,
  useCreateVoiceApiWalletPackageOrderMutation,
  useAdminAdjustWalletBalanceMutation,
  useGetAIAgentSettingQuery,
  useSyncInvoiceMutation,
  useGetOrgMessagingNumbersQuery,
  useSetNumberSmsComplianceMutation,
  useToggleSvoipFeatureMutation,
  useToggleMcpConnectorMutation,
  useToggleAiPersonalAgentMutation,
  useGetCrmSettingByOrgQuery,
  useUpdateCrmSettingMutation,
  // KB trainer (admin)
  useCreateAdminKBSessionMutation,
  useLazyGetAdminKBUploadUrlQuery,
  useUploadAdminKBFileMutation,
  useSendAdminKBMessageMutation,
  useLazyGetAdminKBFilesQuery,
  useLazyGetAdminKBFileStatusQuery,
  useDeleteAdminKBFileMutation,
  useLazyGetAdminKBChangelogQuery,
  useReviewAdminKBChangesMutation,
  usePublishAdminKBSessionMutation,
  useCreateV3OrderMutation,
  useAdminV3QuoteMutation,
  useAdminV3CheckoutAndFulfilMutation,
} = superfoneApi;
